import { useState } from 'react'
import { styled } from '@mui/material/styles'
import { useLocale } from 'src/lib/hooks/useLocal'
import FormTile from 'src/components/common/containers/FormTile'
import { useCartContext } from 'src/lib/contexts/CartContext'
import { Button, Tooltip } from '@mui/material'
import { Plus } from 'react-feather'
import AddMilestoneDialog from './AddMilestoneDialog'
import Milestones from './Milestones'
import { MILESTONE_TYPES, MilestoneCategory } from '@shopflo/ui'
import ConfirmationDialog from 'src/components/common/feedback-and-dialog/ConfirmationDialog'
import SaveNotification from 'src/components/common/SaveNotification'
import { useCartUIPermissions } from 'src/lib/access-permission-hooks/useCartUIPermissions'
import CartNotesSetup from './CartNotesSetup'
import { classNames } from 'src/lib/utils/helpers'

const AddMilestoneButton = styled(Button)({
  '&.MuiButton-root.Mui-disabled': {
    pointerEvents: 'auto',
    border: '0px solid var(--flo-gray-light-color)',
    outline: 'none'
  },
  '&.MuiButton-root.Mui-disabled:hover': {
    background: 'rgba(58, 53, 65, 0.3);'
  }
})

const Widget: React.FC<{}> = () => {
  const { t } = useLocale()
  const {
    state: { isEditingMilestone, progressBar },
    actions: {
      setIsProgressBarEnabled,
      setIsEditingMilestone,
      setDraftMilestone,
      saveProgressbar,
      discardChanges,
      setActiveProgressBar
    },
    getters: {
      canCreateMilestone,
      milestones,
      hasUnsavedCartWidgetChanges,
      isAmountBased,
      isCountBased,
      hasUnsavedCartNoteChanges
    }
  } = useCartContext()

  const { canEditCart } = useCartUIPermissions()

  const [shouldConfirmProgressBarChange, setShouldConfirmProgressBarChange] = useState(false)

  const handleSetActiveProgressBar = (category: MilestoneCategory) => {
    if (!hasUnsavedCartWidgetChanges) {
      setActiveProgressBar(category)
    } else {
      setShouldConfirmProgressBarChange(true)
    }
  }

  return (
    <div className='flex flex-col gap-4 '>
      <CartNotesSetup />
      <div>
        <FormTile
          className='border-b-0 rounded-b-none'
          title={'Type'}
          subtitle={t('progress_bar_type')}
          endComponent={
            <div className='flex justify-center items-center border  rounded-[10px] bg-white text-sm'>
              <button
                onClick={() => handleSetActiveProgressBar(MilestoneCategory.COUNT_BASED)}
                className={classNames(
                  ` px-4 py-1 h-full rounded-tl rounded-bl`,
                  isCountBased ? 'bg-primary-dark text-white font-medium' : 'bg-white text-gray-dark'
                )}
              >
                {t('count_based')}
              </button>
              <button
                onClick={() => handleSetActiveProgressBar(MilestoneCategory.PRICE_BASED)}
                className={classNames(
                  `px-4 py-1 h-full rounded-tr rounded-br`,
                  isAmountBased ? 'bg-primary-dark text-white font-medium' : 'bg-white text-gray-dark'
                )}
              >
                {t('amount_based')}
              </button>
            </div>
          }
        >
          <></>
        </FormTile>
        <ConfirmationDialog
          isOpen={shouldConfirmProgressBarChange}
          setIsConfirmationOpen={() => setShouldConfirmProgressBarChange(false)}
          title={t('progress_bar_in_cart_title')}
          confirmationText={t('progress_bar_category_change_confirmation_message')}
          jobButton={'Save'}
          cancelButton={'Discard'}
          jobButtonColor='primary'
          cancelButtonColor='error'
          handleJob={async () => {
            await saveProgressbar()
            setShouldConfirmProgressBarChange(false)
            setActiveProgressBar(isCountBased ? MilestoneCategory.PRICE_BASED : MilestoneCategory.COUNT_BASED)
          }}
          cancelButtonJob={async () => {
            discardChanges()
            setShouldConfirmProgressBarChange(false)
            setActiveProgressBar(isCountBased ? MilestoneCategory.PRICE_BASED : MilestoneCategory.COUNT_BASED)
          }}
        />
        <FormTile
          title={t('milestones_on_progress_bar_title')}
          subtitle={t('milestones_on_progress_bar_subtitle')}
          className='rounded-none border-b-0'
        >
          <div>
            <Milestones />
            <Tooltip title={!canCreateMilestone && t('milestones_on_progress_bar_tooltip_on_milestones_disabled')}>
              <div className='mt-4 inline-block'>
                <AddMilestoneButton
                  id='create-discount-button'
                  variant={'contained'}
                  sx={{ height: 32 }}
                  className='space-x-2'
                  disabled={canEditCart ? !canCreateMilestone : true}
                  onClick={() => {
                    setDraftMilestone({
                      milestone: Math.max(...milestones.map(milestone => milestone.milestone), 0) + 1,
                      milestone_amount: 0,
                      milestone_header: '',
                      milestone_text: '',
                      milestone_type: MILESTONE_TYPES.FLAT_OFF
                    })
                    setIsEditingMilestone(true)
                  }}
                >
                  {t('add_milestone')}
                  <Plus id='create-discount-button-icon' style={{ height: 18, width: 18, marginLeft: '4px' }} />
                </AddMilestoneButton>
              </div>
            </Tooltip>
          </div>
        </FormTile>
      </div>
      <AddMilestoneDialog isOpen={isEditingMilestone} setIsOpen={setIsEditingMilestone} />
      <SaveNotification
        isVisible={hasUnsavedCartWidgetChanges || hasUnsavedCartNoteChanges}
        onSave={saveProgressbar}
        onDiscard={discardChanges}
        className='w-full'
      />
    </div>
  )
}

export default Widget
